/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { get as getUser } from 'js/lib/user';

import { Button, Dialog, InlineNotification } from '@coursera/cds-core';
import type { Button as ButtonEventType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import useSaveLocalStorageOnboardingToLearnerObject from 'bundles/onboarding-common/hooks/useSaveLocalStorageOnboardingToLearnerObject';
import type { LocalStorageOnboardingDataType } from 'bundles/onboarding-common/types/LocalStorage';
import { clearLocalStorageOnboardingData } from 'bundles/onboarding-common/utils/localStorageUtils';

import _t from 'i18n!nls/onboarding-common';

type Props = {
  localStorageData: LocalStorageOnboardingDataType;
  shouldClearSkills?: boolean;
  onSubmitSuccess: () => void;
  onClose?: () => void;
};

const styles = {
  buttonContainer: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-200);
  `,
};

const OnboardingOverwriteWarningModal = ({ localStorageData, onClose, onSubmitSuccess, shouldClearSkills }: Props) => {
  const { saveLocalStorageOnboardingToLearnerObject } = useSaveLocalStorageOnboardingToLearnerObject();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [notification, setNotification] = useState<'success' | 'error' | null>(null);
  const track = useTracker();

  const PATHWAYS_ONBOARDING_OVERWRITE_MODAL_NAME = 'pathways_onboarding_overwrite';
  const modalEventData = {
    modal: { name: PATHWAYS_ONBOARDING_OVERWRITE_MODAL_NAME },
  } as const;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_modal',
    modalEventData
  );

  const trackClickButton = (buttonName: ButtonEventType['name']) => {
    track('click_button', {
      button: {
        name: buttonName,
      },
      ...modalEventData,
    });
  };

  const onCloseHandler = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleClose = () => {
    track('close_modal', {
      modal: {
        name: PATHWAYS_ONBOARDING_OVERWRITE_MODAL_NAME,
      },
    });
    clearLocalStorageOnboardingData();
    onCloseHandler();
  };

  const handleUpdateGoals = async () => {
    trackClickButton('continue_process');
    setIsSubmitting(true);
    setNotification(null);

    try {
      await saveLocalStorageOnboardingToLearnerObject(
        localStorageData,
        getUser().id,
        getUser().external_id,
        shouldClearSkills
      );
      setNotification('success');
      setTimeout(() => {
        setIsSubmitting(false);
        setIsOpen(false);
        onSubmitSuccess();
      }, 1000);
    } catch {
      setIsSubmitting(false);
      setNotification('error');
    }
  };

  const handleKeepCurrentGoals = () => {
    trackClickButton('onboarding_cancel');
    clearLocalStorageOnboardingData();
    onCloseHandler();
  };

  return (
    <div ref={visibilityTrackingRef}>
      <Dialog open={isOpen} onClose={handleClose} width="medium">
        <Dialog.HeadingGroup>{_t('Are you sure you want to update your recommendations?')}</Dialog.HeadingGroup>
        <Dialog.Content aria-live="assertive">
          {notification === 'success' && (
            <InlineNotification role="alert" severity="success">
              {_t('Your goals have been updated successfully.')}
            </InlineNotification>
          )}
          {notification === 'error' && (
            <InlineNotification role="alert" severity="error">
              {_t('There was an error, please try again.')}
            </InlineNotification>
          )}
        </Dialog.Content>
        <Dialog.Actions css={styles.buttonContainer}>
          <Button variant="primary" onClick={handleUpdateGoals} disabled={isSubmitting}>
            {_t('Yes, update goals')}
          </Button>
          <Button variant="secondary" onClick={handleKeepCurrentGoals} disabled={isSubmitting}>
            {_t('No, keep current goals')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
};

export default OnboardingOverwriteWarningModal;
